import React from "react";
import { Slider, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { useWorkflowStore, Workflow } from "@/stores/WorkflowStore";
import { useChatStore } from "@/stores/ChatStore";
import { isSpecializedWorkflow } from "@/utils/workflowUtils";

const getTemperatureValue = (workflow: Workflow | null): number => {
  if (!workflow || !workflow.agents) return 0.7; // default value

  // Find the first agent with temperature parameter
  for (const agent of Object.values(workflow.agents)) {
    if (
      agent.parameters &&
      agent.parameters.temperature !== undefined &&
      agent.parameters.temperature !== null
    ) {
      const temp = parseFloat(String(agent.parameters.temperature));

      return isNaN(temp) ? 0.7 : temp;
    }
  }

  return 0.7; // default value
};

const getTemperatureAgentId = (workflow: Workflow | null): string | null => {
  if (!workflow || !workflow.agents) return null;

  for (const [agentId, agent] of Object.entries(workflow.agents)) {
    if (
      agent.parameters &&
      agent.parameters.temperature !== undefined &&
      agent.parameters.temperature !== null
    ) {
      return agentId;
    }
  }

  return null;
};

export const TemperatureSelector: React.FC<any> = () => {
  const { workflow, setWorkflow } = useWorkflowStore();
  const { runId } = useChatStore();
  const { t } = useTranslation();

  const temperatureValue = getTemperatureValue(workflow);
  const temperatureAgentId = getTemperatureAgentId(workflow);

  // Determine disable reasons and tooltip message
  const isDisabledByRun = !!runId;
  const isSpecialized = isSpecializedWorkflow(workflow);
  const isDisabled = isSpecialized || isDisabledByRun;

  const tooltipMessage = isSpecialized
    ? t("disabled-temperature-workflow")
    : isDisabledByRun
      ? t("disabled-temperature-selector")
      : "";
  const updateAgentParameter = (agent_id: string, param: string, value: string) => {
    if (!workflow || !agent_id) return;

    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            [param]: value,
          },
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const sliderComponent = (
    <div>
      <div className="text-xs text-left leading-relaxed max-w-xs whitespace-pre-wrap p-2 text-brand-primary">
        <span className="font-bold text-sm">{t("Temperature")}</span>
        <br />
        {t("Controls creativity")}:
        <br />◦ {t("Lower (0) = more precise and consistent")}.
        <br />◦ {t("Higher (1) = more random and creative")}.
      </div>
      <Slider
        className={"max-w-md"}
        data-id={`param-temperature-slider`}
        defaultValue={temperatureValue}
        isDisabled={isDisabled}
        maxValue={1}
        minValue={0}
        showSteps={true}
        step={0.1}
        value={temperatureValue}
        onChange={(val) =>
          temperatureAgentId && updateAgentParameter(temperatureAgentId, "temperature", String(val))
        }
      />
    </div>
  );

  // Return with tooltip if disabled, otherwise just the component
  if (isDisabled && tooltipMessage) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <div>{sliderComponent}</div>
        </TooltipTrigger>
        <TooltipContent showArrow className="max-w-[280px]" placement="bottom">
          {tooltipMessage}
        </TooltipContent>
      </Tooltip>
    );
  }

  return sliderComponent;
};
